import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as fbSignOut,
  onIdTokenChanged,
  type Auth,
  type User,
} from "firebase/auth";
import { config } from "@/config";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export function getFirebaseAuth(): Auth {
  if (!auth) {
    app = initializeApp({
      apiKey: config.firebase.apiKey,
      authDomain: config.firebase.authDomain,
      projectId: config.firebase.projectId,
      appId: config.firebase.appId,
    });
    auth = getAuth(app);
  }
  return auth;
}

const googleProvider = new GoogleAuthProvider();
// Desks juggle multiple Google accounts — always show the account chooser.
googleProvider.setCustomParameters({ prompt: "select_account" });

export function signInWithEmail(email: string, password: string): Promise<User> {
  return signInWithEmailAndPassword(getFirebaseAuth(), email, password).then((c) => c.user);
}

/** Self-serve registration — the backend auto-provisions the account as PENDING on first token. */
export function signUpWithEmail(email: string, password: string): Promise<User> {
  return createUserWithEmailAndPassword(getFirebaseAuth(), email, password).then((c) => c.user);
}

export async function signInWithGoogle(): Promise<User> {
  try {
    const c = await signInWithPopup(getFirebaseAuth(), googleProvider);
    return c.user;
  } catch (e) {
    const code = (e as { code?: string })?.code;
    // Compliance-locked desk browsers block popups — fall back to the full-page redirect,
    // which works everywhere. The page navigates away; the session resumes on return.
    if (code === "auth/popup-blocked" || code === "auth/cancelled-popup-request") {
      await signInWithRedirect(getFirebaseAuth(), googleProvider);
      return new Promise<User>(() => {}); // navigation takes over
    }
    throw e;
  }
}

/** Surface errors from a returning Google REDIRECT sign-in (success flows through
 *  onIdTokenChanged on its own; only failures would otherwise vanish silently). */
export function consumeGoogleRedirect(): Promise<unknown> {
  return getRedirectResult(getFirebaseAuth());
}

export function signOut(): Promise<void> {
  return fbSignOut(getFirebaseAuth());
}

/**
 * Token provider for the WsClient. Firebase caches the ID token and refreshes
 * it automatically; `getIdToken()` returns a valid one (refreshing if near
 * expiry). The WS layer calls this on every (re)connect.
 */
export async function getIdToken(): Promise<string> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("not authenticated");
  return user.getIdToken();
}

export function onAuthChanged(cb: (user: User | null) => void): () => void {
  return onIdTokenChanged(getFirebaseAuth(), cb);
}

export type { User };
